import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { parseAsString, useQueryState } from 'nuqs';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { useLocationFilter, DEVICE_LOCATION_LABEL } from '../../ducks/locationFilter.duck';

import { Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import AddressAutoComplete, {
  EMPTY_ADDRESS,
  type AddressType,
} from '../../components/ui/address-autocomplete';
import { Grid, Map as MapIcon, MapPin, Search } from 'lucide-react';

import { QuickFiltersSheet } from '../../components/QuickFiltersSheet';
import { useNearMe, NEAR_ME_STATUS } from '../../hooks/useNearMe';
import { CONDITION_GROUPS } from '../SearchPage/CustomSearchPage/useSearchPage';
import { NuqsAdapter } from './nuqsAdapterV5';
import { fetchMapListings } from './BrowsePage.duck';
import { DISTANCE_STEPS, zoomForRadiusMiles } from './mapConstants';
import FeaturedCarousel from './FeaturedCarousel';
import CategoryGrid from './CategoryGrid';
import MapView from './MapView';
import type { BrowsePageState } from './types';

type ConditionGroup = (typeof CONDITION_GROUPS)[number];

const DEFAULT_DISTANCE_INDEX = 3;

const BrowsePage = () => {
  const dispatch = useDispatch();
  const config = useConfiguration() as any;
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const { viewMode: viewModeParam } = useParams<{ viewMode?: string }>();
  const viewMode = viewModeParam === 'map' ? 'map' : 'grid';

  const browseState = useSelector((state: any) => state.BrowsePage) as BrowsePageState | undefined;
  const featuredListingIds = browseState?.featuredListingIds ?? [];
  const mapPins = browseState?.mapPins ?? [];
  const fetchInProgress = browseState?.fetchInProgress ?? false;
  const mapFetchInProgress = browseState?.mapFetchInProgress ?? false;
  const listings = useSelector((state: any) => getListingsById(state, featuredListingIds));
  const scrollingDisabled = useSelector((state: any) => isScrollingDisabled(state));

  const categories = config?.categoryConfiguration?.categories || [];

  const [searchQuery, setSearchQuery] = useQueryState('keywords', parseAsString.withDefault(''));
  const [selectedCategoryId, setSelectedCategoryId] = useQueryState(
    'pub_categoryLevel1',
    parseAsString.withDefault(''),
  );

  const [conditionGroupLabel, setConditionGroupLabel] = useState<string | null>(null);
  const conditionValues = useMemo(() => {
    const group = CONDITION_GROUPS.find(g => g.label === conditionGroupLabel);
    return group?.values ?? [];
  }, [conditionGroupLabel]);

  const [selectedPrice, setSelectedPrice] = useState<string>('');
  const priceRange = useMemo(() => {
    if (!selectedPrice) return { priceMin: undefined, priceMax: undefined };
    const [min, max] = selectedPrice.split(',').map(Number);
    return { priceMin: min, priceMax: max };
  }, [selectedPrice]);

  const locationFilter = useLocationFilter();

  const distanceIndex = useMemo(() => {
    const miles = locationFilter.distanceMiles;
    if (miles == null) return [DEFAULT_DISTANCE_INDEX];
    const idx = DISTANCE_STEPS.indexOf(miles);
    return [idx >= 0 ? idx : DEFAULT_DISTANCE_INDEX];
  }, [locationFilter.distanceMiles]);
  const displayDistance = DISTANCE_STEPS[distanceIndex[0]];

  const handleSliderChange = (vals: number[]) => {
    locationFilter.setDistance(DISTANCE_STEPS[vals[0]]);
  };

  const nearMe = useNearMe();
  const [locationRequested, setLocationRequested] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');

  const handleAddressSelected = (a: AddressType) => {
    if (!a.lat || !a.lng) return;
    const firstSegment = a.formattedAddress.split(',')[0]?.trim() ?? '';
    const cityOrPlace = a.city || firstSegment;
    const label =
      [cityOrPlace, a.region].filter(Boolean).join(', ') ||
      a.postalCode ||
      a.formattedAddress;
    locationFilter.setTyped({ lat: a.lat, lng: a.lng, label });
    setLocationRequested(false);
  };

  const setDevice = locationFilter.setDevice;
  useEffect(() => {
    if (
      locationRequested &&
      nearMe.status === NEAR_ME_STATUS.Granted &&
      nearMe.coords
    ) {
      setDevice({
        lat: nearMe.coords.latitude,
        lng: nearMe.coords.longitude,
      });
      setLocationRequested(false);
    }
  }, [locationRequested, nearMe.status, nearMe.coords, setDevice]);

  const showNearMeGate =
    locationRequested &&
    nearMe.status === NEAR_ME_STATUS.Denied &&
    !nearMe.isPersistentlyDeclined;

  const isLocationActive = locationFilter.isActive;
  const activeOriginLabel = locationFilter.label;
  const activeOrigin = locationFilter.originString;
  const activeDistance = activeOrigin ? String(displayDistance) : null;

  useEffect(() => {
    if (viewMode !== 'map') return;
    const originMaybe = activeOrigin && activeDistance
      ? { origin: activeOrigin, distance: activeDistance }
      : {};
    dispatch(
      fetchMapListings({
        keywords: searchQuery || undefined,
        conditions: conditionValues.length > 0 ? conditionValues : undefined,
        priceMin: priceRange.priceMin,
        priceMax: priceRange.priceMax,
        ...originMaybe,
      }) as any,
    );
  }, [
    viewMode,
    searchQuery,
    conditionGroupLabel,
    selectedPrice,
    activeOrigin,
    activeDistance,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewMode === 'map' && activeOrigin) {
      const [latStr, lngStr] = activeOrigin.split(',');
      const lat = Number(latStr);
      const lng = Number(lngStr);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        (window as any).mapboxMap?.easeTo({
          center: [lng, lat],
          zoom: zoomForRadiusMiles(displayDistance),
        });
      }
    }
  }, [viewMode, activeOrigin, displayDistance]);

  const requestLocation = () => {
    setLocationRequested(true);
    nearMe.request();
  };

  const dismissLocation = () => {
    nearMe.dismiss();
    setLocationRequested(false);
  };

  const clearLocation = () => {
    setLocationRequested(false);
    locationFilter.clear();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (viewMode !== 'map') history.push(`/browse/map?keywords=${encodeURIComponent(searchQuery.trim())}`);
  };

  const activeFilterCount =
    (conditionGroupLabel ? 1 : 0) + (selectedPrice ? 1 : 0);

  const handleResetAll = () => {
    setConditionGroupLabel(null);
    setSelectedPrice('');
    setSearchQuery('');
    setSelectedCategoryId('');
    clearLocation();
  };

  const mapCountText = mapFetchInProgress
    ? 'Loading…'
    : `${mapPins.length} on map`;

  return (
    <Page title="Browse Materials" scrollingDisabled={scrollingDisabled}>
      <TopbarContainer />

      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h1 className="text-4xl font-heading font-bold uppercase tracking-tight">
            Browse Materials
          </h1>
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative w-64 lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
                className="pl-9 h-10 w-full bg-muted/50 border-muted-foreground/20 focus-visible:ring-1"
                value={searchQuery}
                onChange={e => void setSearchQuery(e.target.value)}
              />
            </form>
            <div className="flex bg-muted/50 p-1 rounded-lg border border-border">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => history.push('/browse')}
                className="gap-1.5 font-heading uppercase tracking-wide text-xs h-8"
              >
                <Grid className="w-3.5 h-3.5" /> Grid View
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => history.push('/browse/map')}
                className="gap-1.5 font-heading uppercase tracking-wide text-xs h-8"
              >
                <MapIcon className="w-3.5 h-3.5" /> Map Search
              </Button>
            </div>
          </div>
        </div>

        {viewMode === 'map' && (
          <div className="flex flex-wrap items-center gap-3 bg-muted/30 px-3 py-2 rounded-lg border border-border mb-3">
            <span className="text-xs text-muted-foreground font-medium">{mapCountText}</span>

            <div className="flex-1" />

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 w-48">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Radius</span>
                <Slider
                  defaultValue={[DEFAULT_DISTANCE_INDEX]}
                  max={7}
                  step={1}
                  value={distanceIndex}
                  onValueChange={handleSliderChange}
                  className="flex-1"
                />
                <span className="text-xs font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded whitespace-nowrap min-w-[52px] text-center">
                  {displayDistance === 150 ? '150+' : displayDistance} mi
                </span>
              </div>

              <div className="hidden md:block w-px h-5 bg-border" />

              {isLocationActive ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 text-primary" />
                    <span>Near {activeOriginLabel}</span>
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearLocation}
                    className="h-7 px-2 text-xs"
                  >
                    Clear
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 w-72">
                  <div className="flex-1">
                    <AddressAutoComplete
                      placeOnly
                      address={EMPTY_ADDRESS}
                      setAddress={handleAddressSelected}
                      searchInput={locationQuery}
                      setSearchInput={setLocationQuery}
                      dialogTitle="Pick a place"
                      placeholder="City, ZIP, or state"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={requestLocation}
                    className="h-9 px-2 gap-1 text-xs whitespace-nowrap"
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    Use mine
                  </Button>
                </div>
              )}

              <div className="hidden md:block w-px h-5 bg-border" />

              <QuickFiltersSheet
                activeFilterCount={activeFilterCount}
                totalItems={mapPins.length}
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                onSelectCategory={(id: string) => setSelectedCategoryId(id)}
                onClearCategory={() => setSelectedCategoryId('')}
                conditionGroupLabel={conditionGroupLabel}
                onSelectConditionGroup={(group: ConditionGroup | null) =>
                  setConditionGroupLabel(group?.label ?? null)
                }
                selectedPrice={selectedPrice}
                onSelectPrice={setSelectedPrice}
                hideNearMeSection
                pendingMiles={null}
                onSelectDistance={() => {}}
                nearMeStatus={nearMe.status}
                isPersistentlyDeclined={nearMe.isPersistentlyDeclined}
                onAllowLocation={nearMe.request}
                onDismissLocation={dismissLocation}
                isLocationActive={isLocationActive}
                activeLocationLabel={activeOriginLabel || DEVICE_LOCATION_LABEL}
                onClearLocation={clearLocation}
                onReset={handleResetAll}
              />
            </div>

            {showNearMeGate && (
              <div className="w-full">
                <p className="text-xs text-muted-foreground">
                  Location permission denied. Pick a place above instead.
                </p>
              </div>
            )}
          </div>
        )}

        {viewMode === 'map' ? (
          <MapView pins={mapPins} routeConfiguration={routeConfiguration} history={history} intl={intl} />
        ) : (
          <>
            {listings.length > 0 && <FeaturedCarousel listings={listings} />}

            {fetchInProgress && listings.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading featured listings...</p>
              </div>
            )}

            <CategoryGrid categories={categories} />
          </>
        )}
      </main>

      <FooterContainer />
    </Page>
  );
};

export default function BrowsePageWithNuqs(props: any) {
  return (
    <NuqsAdapter>
      <BrowsePage {...props} />
    </NuqsAdapter>
  );
}
